import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Inbox, Search, UserPlus, UserCheck } from "lucide-react";

const SRC_LABEL = { li: "LinkedIn", ig: "Instagram", x: "X", fb: "Facebook", direct: "Direct", unknown: "Unknown", invalid: "Invalid" };
const srcName = (s) => SRC_LABEL[s] ? `${SRC_LABEL[s]}${SRC_LABEL[s].toLowerCase() !== s ? ` (${s})` : ""}` : s;
const fmt = (d) => (d ? new Date(d).toLocaleString("pl-PL") : "—");

export const SourceTag = ({ s }) => (
  <span className={`rounded-full text-[11px] px-2 py-0.5 font-[Manrope] font-medium ${s === "unknown" || s === "invalid" ? "bg-zinc-100 text-zinc-500" : s === "direct" ? "bg-sky-50 text-sky-700" : "bg-[#193962]/10 text-[#193962]"}`}>{srcName(s)}</span>
);

function SourceStats({ stats, active, onPick }) {
  return (
    <div className="flex flex-wrap gap-2 mb-5" data-testid="lead-source-stats">
      <span className="text-xs text-muted-foreground font-[Manrope] self-center mr-1">Last 30 days:</span>
      {stats.length === 0 && <span className="text-xs text-muted-foreground">no leads</span>}
      {stats.map((r) => (
        <button key={r.source} onClick={() => onPick(active === r.source ? "all" : r.source)} data-testid={`lead-stat-${r.source}`}
          className={`text-xs font-[Manrope] px-3 py-1 rounded-full border transition-colors ${active === r.source ? "bg-[#193962] text-white border-[#193962]" : "bg-white border-border hover:border-[#193962]"}`}>
          {srcName(r.source)} <strong className="ml-1">{r.count}</strong>
        </button>
      ))}
    </div>
  );
}

function ConvertAction({ lead, onDone }) {
  const [busy, setBusy] = useState(false);
  if (lead.client_id) return <Link to={`/clients/${lead.client_id}`} data-testid="lead-open-client" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#193962] underline"><UserCheck className="h-4 w-4" />Open client card</Link>;
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/leads/${lead.lead_id}/convert`);
      toast.success(r.data.linked_existing ? "Linked to existing client with this email" : "Client card created");
      onDone(r.data.client_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Conversion failed"); } finally { setBusy(false); }
  };
  return <Button onClick={go} disabled={busy} data-testid="lead-convert-btn" className="bg-[#193962] text-white"><UserPlus className="h-4 w-4 mr-1.5" />{busy ? "Converting…" : "Convert to client"}</Button>;
}

function LeadDetail({ lead, onClose, onConverted }) {
  const rows = lead ? [["Email", lead.email], ["Company", lead.company], ["Marketing source", <SourceTag key="s" s={lead.marketing_source} />],
    ["Form type", lead.source], ["Event", lead.event_label || lead.event_slug], ["Language", lead.language], ["Status", lead.status],
    ["Submitted", fmt(lead.submitted_at || lead.created_at)], ["Follow-up", lead.follow_up_sent ? `sent ${fmt(lead.follow_up_sent_at)}` : (lead.follow_up_at ? `planned ${fmt(lead.follow_up_at)}` : "—")]] : [];
  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]" data-testid="lead-detail-name">{lead?.name || lead?.email}</DialogTitle>
          <DialogDescription className="font-[Manrope]">Lead from path.com.pl</DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-[130px_1fr] gap-y-2 text-sm">
          {rows.map(([k, v]) => [<dt key={k} className="text-muted-foreground">{k}</dt>, <dd key={`${k}v`} className="min-w-0 break-words" data-testid={`lead-detail-${k.toLowerCase().replace(/\s/g, "-")}`}>{v || "—"}</dd>])}
        </dl>
        {lead && <div className="pt-2"><ConvertAction lead={lead} onDone={(cid) => onConverted(lead.lead_id, cid)} /></div>}
        {lead?.admin_url && <a href={lead.admin_url} target="_blank" rel="noreferrer" className="text-sm underline text-[#193962]" data-testid="lead-admin-link">Open on website admin</a>}
      </DialogContent>
    </Dialog>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ last_30_days: [], all_sources: [] });
  const [source, setSource] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(null);
  const converted = (id, cid) => {
    const upd = (l) => (l.lead_id === id ? { ...l, client_id: cid, status: "converted" } : l);
    setLeads((ls) => ls.map(upd));
    setOpen((o) => (o ? upd(o) : o));
  };

  useEffect(() => { api.get("/leads/source-stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/leads", { params: { source, search } }).then((r) => setLeads(r.data.leads)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [source, search]);

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8" data-testid="leads-page">
        <div className="flex items-center gap-2.5 mb-5">
          <Inbox className="h-5 w-5 text-[#193962]" />
          <h1 className="font-[Outfit] text-2xl font-semibold tracking-tight">Leads</h1>
          <span className="text-xs text-muted-foreground font-[Manrope] ml-2">RSVP & bookings from path.com.pl</span>
        </div>
        <SourceStats stats={stats.last_30_days} active={source} onPick={setSource} />
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
            <Input data-testid="lead-search" placeholder="Search name, email, company, event" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" />
          </div>
          <select data-testid="lead-source-filter" value={source} onChange={(e) => setSource(e.target.value)} className="h-10 border border-border rounded-md px-2 bg-white text-sm">
            <option value="all">All sources</option>
            {stats.all_sources.map((s) => <option key={s} value={s}>{srcName(s)}</option>)}
          </select>
        </div>
        <div className="border border-border bg-white rounded-md divide-y divide-border" data-testid="lead-list">
          {leads.length === 0 && <p className="p-5 text-sm text-muted-foreground font-[Manrope]" data-testid="lead-empty">No leads match.</p>}
          {leads.map((l) => (
            <button key={l.lead_id} onClick={() => setOpen(l)} data-testid={`lead-row-${l.lead_id}`} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 transition-colors">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate" data-no-i18n>{l.name || l.email}{l.company ? <span className="text-zinc-500 font-normal"> · {l.company}</span> : null}</p>
                <p className="text-[11px] text-zinc-500 truncate">{l.email} · {l.event_label || l.event_slug || l.source} · {fmt(l.submitted_at || l.created_at)}</p>
              </div>
              {l.client_id && <span data-testid={`lead-client-tag-${l.lead_id}`} className="rounded-full text-[11px] px-2 py-0.5 bg-emerald-50 text-emerald-700 font-medium">Client</span>}
              <span data-testid={`lead-source-${l.lead_id}`}><SourceTag s={l.marketing_source} /></span>
            </button>
          ))}
        </div>
      </main>
      <LeadDetail lead={open} onClose={() => setOpen(null)} onConverted={converted} />
    </div>
  );
}
